import React, { useEffect, useState } from 'react'
import { ActivityIndicator, ScrollView, StyleSheet, View } from 'react-native'
import { useTheme } from '@react-navigation/native'
import { Button, Icon, Text } from '@components'
import { useLanguage } from '@context'
import { Theme } from '@theme'
import { ScreenProps } from '@navigation'
import { formatCountdown, formatEventPrice, getCommonStyles } from '@utils'
import {
  useCompleteSimulatedPayment,
  useCountdown,
  useSimulatedPayment,
  useStartEventPayment,
} from '@hooks'

type Outcome = 'paid' | 'failed'

/**
 * `/pay/simulated/:paymentId` — stands in for the payment provider's hosted
 * page (Stripe Checkout, later). Nothing is charged: "Payer" and "Simuler un
 * échec" call the same route a real provider's webhook will reach.
 */
export const SimulatedPayment: React.FC<ScreenProps<'SimulatedPayment'>> = ({
  route,
  navigation,
}) => {
  const theme = useTheme()
  const { t, currentLanguage } = useLanguage()
  const styles = getStyles(theme)
  const paymentId = String(route.params?.paymentId ?? '')
  const { data, isLoading, isError } = useSimulatedPayment(paymentId)
  const complete = useCompleteSimulatedPayment()
  const { startPayment, isStarting } = useStartEventPayment()
  const [failed, setFailed] = useState(false)
  const remaining = useCountdown(data?.payment.expiresAt)

  // "Réessayer" opens a NEW payment on this same screen: start from a clean state.
  useEffect(() => setFailed(false), [paymentId])

  const backToEvent = (payment?: 'success' | 'cancelled') =>
    navigation.reset({
      index: 0,
      routes: [{ name: 'PublicEvent', params: { code: data?.event.code ?? '', payment } }],
    })

  if (isLoading) {
    return (
      <View style={styles.center}>
        <ActivityIndicator color={theme.colors.primary} />
      </View>
    )
  }

  if (isError || !data) {
    return (
      <View style={styles.center}>
        <Text type="titleBold" center>
          {t('events.payment.notFound')}
        </Text>
      </View>
    )
  }

  const { payment, event } = data
  const amount = formatEventPrice(payment.amountCents, currentLanguage, payment.currency)
  const expired = payment.status === 'expired' || (payment.status === 'pending' && remaining <= 0)
  const isFailed = failed || payment.status === 'failed'

  const pay = (outcome: Outcome) =>
    complete.mutate(
      { paymentId, outcome },
      {
        onSuccess: ({ payment: settled }) => {
          if (settled.status === 'paid') backToEvent('success')
          else setFailed(true)
        },
      },
    )

  let body: React.ReactNode
  if (payment.status === 'paid') {
    body = (
      <>
        <Text type="titleBold" center testID="payment-already-paid">
          {t('events.payment.alreadyPaid')}
        </Text>
        <Button title={t('events.payment.backToEvent')} onPress={() => backToEvent()} />
      </>
    )
  } else if (isFailed) {
    body = (
      <>
        <View style={styles.resultIcon}>
          <Icon name="cross" size={28} color={theme.colors.error} />
        </View>
        <Text type="titleBold" center testID="payment-failed">
          {t('events.payment.failedTitle')}
        </Text>
        <Text type="body" color={theme.colors.secondary} center>
          {t('events.payment.failedHint')}
        </Text>
        <Button
          title={t('events.payment.retry')}
          testID="payment-retry"
          disabled={isStarting}
          onPress={() => void startPayment({ id: event.id })}
          containerStyle={styles.fullWidth}
        />
        <Button
          title={t('events.payment.backToEvent')}
          unfilled
          onPress={() => backToEvent('cancelled')}
          containerStyle={styles.fullWidth}
        />
      </>
    )
  } else if (expired) {
    body = (
      <>
        <Text type="titleBold" center testID="payment-expired">
          {t('events.payment.expiredTitle')}
        </Text>
        <Text type="body" color={theme.colors.secondary} center>
          {t('events.payment.expiredHint')}
        </Text>
        <Button
          title={t('events.payment.retry')}
          disabled={isStarting}
          onPress={() => void startPayment({ id: event.id })}
          containerStyle={styles.fullWidth}
        />
      </>
    )
  } else {
    body = (
      <>
        <Text type="bodySmallBold" color={theme.colors.greyBlue} center>
          {t('events.payment.forEvent')}
        </Text>
        <Text type="titleBold" center>
          {event.name}
        </Text>
        <View style={styles.breakdown} testID="payment-breakdown">
          <View style={styles.line}>
            <Text type="body" color={theme.colors.secondary}>
              {t('events.payment.priceLine')}
            </Text>
            <Text type="bodyBold" testID="payment-price-line">
              {formatEventPrice(payment.priceCents, currentLanguage, payment.currency)}
            </Text>
          </View>
          {payment.serviceFeeCents > 0 ? (
            <View style={styles.line}>
              <Text type="body" color={theme.colors.secondary}>
                {t('events.payment.feeLine')}
              </Text>
              <Text type="bodyBold" testID="payment-fee-line">
                {formatEventPrice(payment.serviceFeeCents, currentLanguage, payment.currency)}
              </Text>
            </View>
          ) : null}
          <View style={[styles.line, styles.totalLine]}>
            <Text type="subtitleBold">{t('events.payment.totalLine')}</Text>
            <Text type="titleBold" color={theme.colors.primary} testID="payment-amount">
              {amount}
            </Text>
          </View>
        </View>
        <View style={styles.timer} testID="payment-timer">
          <Icon name="clock" size={16} color={theme.colors.orange} />
          <Text type="bodySmallBold" color={theme.colors.orange} style={styles.timerText}>
            {t('events.payment.seatHeld', { time: formatCountdown(remaining) })}
          </Text>
        </View>
        <Button
          title={t('events.payment.pay', { amount })}
          testID="payment-pay"
          disabled={complete.isPending}
          onPress={() => pay('paid')}
          containerStyle={styles.fullWidth}
        />
        <Button
          title={t('events.payment.simulateFailure')}
          testID="payment-fail"
          unfilled
          disabled={complete.isPending}
          onPress={() => pay('failed')}
          containerStyle={styles.fullWidth}
        />
        <Button
          title={t('events.payment.cancel')}
          testID="payment-cancel"
          isSmall
          unfilled
          disabled={complete.isPending}
          onPress={() => backToEvent('cancelled')}
        />
      </>
    )
  }

  return (
    <ScrollView style={styles.page} contentContainerStyle={styles.scroll}>
      <View style={styles.card}>
        <View style={styles.banner} testID="payment-simulation-banner">
          <Icon name="alertTriangle" size={18} color={theme.colors.black} />
          <Text type="bodyBold" color={theme.colors.black} style={styles.bannerText}>
            {t('events.payment.simulationBanner')}
          </Text>
        </View>
        <View style={styles.body}>{body}</View>
      </View>
    </ScrollView>
  )
}

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    page: { flex: 1, backgroundColor: theme.colors.pageBackground },
    scroll: {
      flexGrow: 1,
      alignItems: 'center',
      justifyContent: 'center',
      padding: theme.spacing.lg,
    },
    center: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      padding: theme.spacing.xxl,
      backgroundColor: theme.colors.pageBackground,
    },
    card: {
      width: '100%',
      maxWidth: 460,
      backgroundColor: theme.colors.white,
      borderRadius: theme.border.jumbo,
      borderWidth: 1,
      borderColor: theme.colors.grey,
      overflow: 'hidden',
      ...getCommonStyles(theme).shadow,
    },
    banner: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: theme.spacing.sm,
      backgroundColor: theme.colors.yellow,
      paddingVertical: theme.spacing.md,
      paddingHorizontal: theme.spacing.lg,
    },
    bannerText: { flexShrink: 1, textAlign: 'center' },
    body: { padding: theme.spacing.xxl, gap: theme.spacing.md, alignItems: 'stretch' },
    timer: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      alignSelf: 'center',
    },
    timerText: { marginLeft: theme.spacing.xs },
    breakdown: {
      gap: theme.spacing.sm,
      padding: theme.spacing.lg,
      borderRadius: theme.border.medium,
      backgroundColor: theme.colors.sidebar,
    },
    line: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
    totalLine: {
      borderTopWidth: 1,
      borderTopColor: theme.colors.grey,
      paddingTop: theme.spacing.sm,
    },
    resultIcon: {
      alignSelf: 'center',
      width: 56,
      height: 56,
      borderRadius: 28,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.colors.errorSoft,
    },
    fullWidth: { width: '100%' },
  })
